export enum Environment {
  Production = 'production',
  Candidate = 'candidate',
  UAT = 'uat',
  EU = 'eu',
  Main = 'main',
  QA1 = 'qa1',
  QA2 = 'qa2',
}

const baseUrls = {
  [Environment.Production]: `https://prostudio.pypestream.com`,
  [Environment.EU]: `https://prostudio.pypestream.eu`,
  [Environment.Candidate]: `https://prostudio.candidate.pypestream.dev`,
  [Environment.Main]: `https://prostudio.main.pypestream.dev`,
  [Environment.UAT]: `https://prostudio.uat.pypestream.dev`,
  [Environment.QA1]: `https://prostudio.qa1.pypestream.dev`,
  [Environment.QA2]: `https://prostudio.qa2.pypestream.dev`,
};

const hostOf = (url: string): string | null => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

/**
 * The environment a Pro Studio address belongs to: its own host, or any
 * subdomain of it. Branch previews are aliased as subdomains (the bobcat
 * preview is `bobcat.prostudio.candidate.pypestream.dev`); matched by prefix
 * only, that read as no environment, fell back to main, and every test chat
 * went to main's Engagement API — a 404 for a channel main has never heard of.
 */
export const getEnvironmentFromUrl = (url: string): Environment | null => {
  const host = hostOf(url);
  if (!host) return null;
  return (
    (Object.entries(baseUrls).find(([, baseUrl]) => {
      const baseHost = hostOf(baseUrl);
      return !!baseHost && (host === baseHost || host.endsWith(`.${baseHost}`));
    })?.[0] as Environment) || null
  );
};

/**
 * On a local dev server the page's address names no environment, but the
 * backend it's pointed at does: `FE_GQL_ENDPOINT` is
 * `https://graphql.<env>.pypestream.dev/…`. Without this, localhost always
 * read as main — so with the local app on candidate's backend, every test
 * chat went to main's Engagement API and got a 404 for a solution main has
 * never heard of.
 */
export const localBackendEnvironment = (
  endpoint: unknown = import.meta.env?.FE_GQL_ENDPOINT
): Environment | null => {
  const m = /\.(candidate|uat|main|qa1|qa2)\.pypestream\.dev/.exec(
    String(endpoint ?? '')
  );
  return m ? (m[1] as Environment) : null;
};

const isLocalOrigin = (origin: string) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/.test(origin);

/**
 * The environment an origin belongs to: its own, if it's a known Pro Studio
 * host; for a local dev server, the backend it's configured against; main
 * otherwise.
 */
export const resolveEnvironment = (origin: string): Environment =>
  getEnvironmentFromUrl(origin) ??
  (isLocalOrigin(origin) ? localBackendEnvironment() : null) ??
  Environment.Main;

export const getLauncherUrls = (origin: string) => {
  const env = resolveEnvironment(origin);

  const baseUrl = env === Environment.Production ? '' : `${env}.`;
  const topLevelDomain = env === Environment.Production ? 'com' : 'dev';

  return {
    live: `https://web.${baseUrl}pypestream.${topLevelDomain}`,
    sandbox: `https://web-sandbox.${baseUrl}pypestream.${topLevelDomain}`,
  };
};

/**
 * The real, running solution for a channel — the launcher's `preview.html`
 * harness booted against that channel's app id. This is the "live solution"
 * link, NOT the static mock rendered by `<PypePreview/>` inside the settings
 * form: opening it starts an actual conversation with the deployed channel.
 *
 * Shared by the channels-list row action and the Channel Settings "Open Real
 * Preview" block so the two entry points cannot drift apart (PSTUDIO-3768).
 */
export const buildLivePreviewUrl = (
  origin: string,
  env: 'live' | 'sandbox',
  channelId: string
) => `${getLauncherUrls(origin)[env]}/preview.html?id=${channelId}`;

/**
 * Pype Next (pypenext) origin for the current environment AND stack.
 *
 * Mirrors `buildEngagementApiUrl` — pypenext is deployed as two stacks per env
 * (live + sandbox), and each stack advertises its own loader URL, bakes in its
 * own engagement API, and serves its own preview harness. So a sandbox channel
 * must be pointed at the sandbox stack or its harness talks to the wrong
 * backend. The `-sandbox` suffix is applied exactly as the engagement-api URL
 * does it.
 *
 * e.g. production live    -> https://pypenext.pypestream.com
 *      production sandbox -> https://pypenext-sandbox.pypestream.com
 *      main sandbox       -> https://pypenext-sandbox.main.pypestream.dev
 * Localhost/unknown origins fall back to Main.
 */
export const getPypenextBaseUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox'
): string => {
  const env = resolveEnvironment(origin);
  const sandboxSuffix = channelEnv === 'sandbox' ? '-sandbox' : '';

  switch (env) {
    case Environment.Production:
      return `https://pypenext${sandboxSuffix}.pypestream.com`;
    case Environment.EU:
      return `https://pypenext${sandboxSuffix}.pypestream.eu`;
    case Environment.Candidate:
    case Environment.UAT:
    case Environment.Main:
    case Environment.QA1:
    case Environment.QA2:
      return `https://pypenext${sandboxSuffix}.${env}.pypestream.dev`;
    default:
      return `https://pypenext${sandboxSuffix}.main.pypestream.dev`;
  }
};

/** Pype Next docs site for the channel's environment + stack. */
export const getPypenextDocsUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox'
): string => `${getPypenextBaseUrl(origin, channelEnv)}/docs`;

/**
 * The Pype Next preview harness, pre-seeded with the channel's app id.
 *
 * The harness reads `?appId` (URL wins over its own localStorage) and boots on
 * load, so this link starts a real conversation with THIS channel without the
 * user pasting anything. Distinct from `buildLivePreviewUrl`, which is the
 * legacy (Pype 1.0) launcher harness.
 */
export const getPypenextHarnessUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox',
  appId: string
): string =>
  `${getPypenextBaseUrl(origin, channelEnv)}/preview/harness.html?appId=${encodeURIComponent(appId)}`;

/**
 * The loader script a customer imports from. Goes into the embed snippet, so it
 * must be the moving per-env alias — never a per-PR pinned origin.
 */
export const getPypeNextLoaderUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox'
): string => `${getPypenextBaseUrl(origin, channelEnv)}/loader.js`;

/** This environment's Analytics. */
const analyticsOrigin = (origin: string): string => {
  const env = resolveEnvironment(origin);
  const host =
    env === Environment.Production
      ? 'analytics.pypestream.com'
      : env === Environment.EU
        ? 'analytics.pypestream.eu'
        : `analytics.${env}.pypestream.dev`;
  return `https://${host}`;
};

/**
 * Analytics for the current environment, opened on this project — the same
 * `/api/projects/<id>/change` link Manager's project page uses, which switches
 * Analytics to the project before showing it.
 */
export const getAnalyticsProjectUrl = (
  origin: string,
  projectId: string
): string =>
  `${analyticsOrigin(origin)}/api/projects/${encodeURIComponent(projectId)}/change`;

/**
 * One Analytics dashboard: `/dashboard/<project>/<dataset>/<dashboard>`, the
 * path Analytics itself uses (a project's datasets are its Live and Sandbox).
 */
export const getAnalyticsDashboardUrl = (
  origin: string,
  {
    projectId,
    datasetId,
    dashboardId,
  }: { projectId: string; datasetId: number; dashboardId: number }
): string =>
  `${analyticsOrigin(origin)}/dashboard/${encodeURIComponent(projectId)}/${datasetId}/${dashboardId}`;
